/**
 * @fileoverview Confirmation Modal Component
 *
 * Specialized modal for user confirmations with customizable actions.
 * Supports different types (info, warning, danger) with appropriate styling.
 *
 * Features:
 * - Customizable title and message
 * - Rich HTML content support
 * - Different confirmation types (info, warning, danger)
 * - Flexible action buttons
 * - Loading states
 * - Icon support
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import { FiAlertTriangle, FiInfo, FiAlertCircle } from 'react-icons/fi';

/**
 * Confirmation Modal Component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function called when modal should close
 * @param {string} props.title - Modal title
 * @param {string} props.message - Main message text
 * @param {string} props.details - Additional details (supports HTML)
 * @param {string} props.type - Confirmation type: 'info', 'warning', 'danger'
 * @param {string} props.confirmText - Text for confirm button
 * @param {string} props.cancelText - Text for cancel button
 * @param {function} props.onConfirm - Function called when confirmed
 * @param {boolean} props.loading - Whether action is in progress
 * @param {boolean} props.disabled - Whether confirm button is disabled
 * @param {ReactNode} props.icon - Custom icon (overrides type icon)
 */
const ConfirmationModal = ({
  isOpen = false,
  onClose,
  title = 'Confirm Action',
  message = 'Are you sure you want to continue?',
  details = '',
  type = 'info',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  loading = false,
  disabled = false,
  icon = null
}) => {
  const handleConfirm = () => {
    if (onConfirm && !loading && !disabled) {
      onConfirm();
    }
  };

  // Get icon based on type
  const getIcon = () => {
    if (icon) return icon;

    switch (type) {
      case 'warning':
        return <FiAlertTriangle />;
      case 'danger':
        return <FiAlertCircle />;
      case 'info':
      default:
        return <FiInfo />;
    }
  };

  // Get CSS classes based on type
  const getTypeClasses = () => {
    const baseClass = 'confirmation-modal';
    return `${baseClass} ${baseClass}--${type}`;
  };

  const getConfirmButtonClasses = () => {
    const baseClass = 'confirmation-button confirmation-button--confirm';
    return `${baseClass} confirmation-button--${type}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      className={getTypeClasses()}
      closeOnBackdrop={!loading}
      closeOnEscape={!loading}
    >
      <div className="confirmation-header">
        <div className="confirmation-icon">
          {getIcon()}
        </div>
        <div className="confirmation-title-section">
          <h2 className="confirmation-title" id="modal-title">
            {title}
          </h2>
          <p className="confirmation-message">
            {message}
          </p>
        </div>
      </div>

      {details && (
        <div
          className="confirmation-details"
          dangerouslySetInnerHTML={{ __html: details }}
        />
      )}

      <div className="confirmation-actions">
        <button
          type="button"
          className="confirmation-button confirmation-button--cancel"
          onClick={onClose}
          disabled={loading}
        >
          {cancelText}
        </button>
        <button
          type="button"
          className={getConfirmButtonClasses()}
          onClick={handleConfirm}
          disabled={loading || disabled}
        >
          {loading && (
            <span className="button-spinner" />
          )}
          <span>{loading ? 'Processing...' : confirmText}</span>
        </button>
      </div>
    </Modal>
  );
};

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  details: PropTypes.string,
  type: PropTypes.oneOf(['info', 'warning', 'danger']),
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  icon: PropTypes.node
};

ConfirmationModal.defaultProps = {
  isOpen: false,
  title: 'Confirm Action',
  message: 'Are you sure you want to continue?',
  details: '',
  type: 'info',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  onConfirm: null,
  loading: false,
  disabled: false,
  icon: null
};

export default ConfirmationModal;